import { useState } from "react";
import { motion } from "framer-motion";
import { Search, Check, Pencil, Globe, Factory, FileText } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EU_COUNTRIES = [
  "Austria", "Belgium", "Bulgaria", "Croatia", "Cyprus", "Czech Republic",
  "Denmark", "Estonia", "Finland", "France", "Germany", "Greece", "Hungary",
  "Ireland", "Italy", "Latvia", "Lithuania", "Luxembourg", "Malta",
  "Netherlands", "Poland", "Portugal", "Romania", "Slovakia", "Slovenia",
  "Spain", "Sweden", "United Kingdom"
];

export function Step1CompanyDiscovery({ userId, onComplete }) {
  const [companyName, setCompanyName] = useState("");
  const [country, setCountry] = useState("");
  const [loading, setLoading] = useState(false);
  const [discovered, setDiscovered] = useState(null);
  const [editing, setEditing] = useState(false);
  const [editData, setEditData] = useState({});

  const handleDiscover = async () => {
    if (!companyName.trim() || !country) {
      toast.error("Please enter company name and select a country");
      return;
    }
    setLoading(true);
    try {
      // Get work email from localStorage (captured on landing page)
      const workEmail = localStorage.getItem("olana_email") || "";
      
      const { data } = await axios.post(`${API}/company/discover`, {
        user_id: userId,
        company_name: companyName.trim(),
        country,
        work_email: workEmail,
      });
      setDiscovered(data);
      setEditData({ name: data.name, website: data.website, industry: data.industry, description: data.description });
    } catch (err) {
      toast.error("Could not discover company. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = async () => {
    const payload = editing ? editData : { name: discovered.name, website: discovered.website, industry: discovered.industry, description: discovered.description };
    try {
      const { data } = await axios.post(`${API}/company/confirm`, {
        company_id: discovered.id,
        ...payload,
      });
      onComplete(data);
    } catch (err) {
      toast.error("Could not confirm company.");
    }
  };

  if (discovered) {
    const d = editing ? editData : discovered;
    return (
      <div className="space-y-8">
        <div>
          <p className="text-sm text-muted-foreground uppercase tracking-widest mb-2">Here's what we found</p>
          <h2 className="text-3xl md:text-5xl font-semibold tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
            {d.name}
          </h2>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="glass-card p-6 md:p-8 space-y-5"
          data-testid="company-card"
        >
          {editing ? (
            <div className="space-y-4">
              <div>
                <label className="text-sm text-muted-foreground mb-1 block">Company Name</label>
                <input data-testid="edit-name-input" className="hero-input h-12 text-base" value={editData.name} onChange={(e) => setEditData({ ...editData, name: e.target.value })} />
              </div>
              <div>
                <label className="text-sm text-muted-foreground mb-1 block">Website</label>
                <input data-testid="edit-website-input" className="hero-input h-12 text-base" value={editData.website} onChange={(e) => setEditData({ ...editData, website: e.target.value })} />
              </div>
              <div>
                <label className="text-sm text-muted-foreground mb-1 block">Industry</label>
                <input data-testid="edit-industry-input" className="hero-input h-12 text-base" value={editData.industry} onChange={(e) => setEditData({ ...editData, industry: e.target.value })} />
              </div>
              <div>
                <label className="text-sm text-muted-foreground mb-1 block">Description</label>
                <textarea data-testid="edit-description-input" className="hero-input h-24 text-base py-3 resize-none" value={editData.description} onChange={(e) => setEditData({ ...editData, description: e.target.value })} />
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="flex items-center gap-3 text-white/80">
                <Globe className="w-5 h-5 text-primary/70" />
                <span>{d.website}</span>
              </div>
              <div className="flex items-center gap-3 text-white/80">
                <Factory className="w-5 h-5 text-primary/70" />
                <span>{d.industry}</span>
              </div>
              <div className="flex items-start gap-3 text-white/60">
                <FileText className="w-5 h-5 text-primary/70 mt-0.5" />
                <span className="leading-relaxed">{d.description}</span>
              </div>
            </div>
          )}
        </motion.div>

        <div className="flex gap-3">
          <Button
            data-testid="edit-btn"
            variant="outline"
            onClick={() => setEditing(!editing)}
            className="rounded-full px-6 border-white/10 hover:bg-white/5"
          >
            <Pencil className="w-4 h-4 mr-2" />
            {editing ? "Cancel" : "Edit"}
          </Button>
          <Button
            data-testid="confirm-btn"
            onClick={handleConfirm}
            className="rounded-full px-8 bg-primary text-primary-foreground btn-glow"
          >
            <Check className="w-4 h-4 mr-2" />
            {editing ? "Save & Continue" : "Looks good, continue"}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div>
        <p className="text-sm text-muted-foreground uppercase tracking-widest mb-2">Let's start</p>
        <h2 className="text-3xl md:text-5xl font-semibold tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
          Tell us about your company
        </h2>
        <p className="text-base text-muted-foreground mt-3 leading-relaxed">
          We'll find relevant sustainability insights for your business.
        </p>
      </div>

      <div className="space-y-4 max-w-lg">
        <div>
          <label className="text-sm text-muted-foreground mb-2 block">Company Name</label>
          <input
            data-testid="company-name-input"
            type="text"
            value={companyName}
            onChange={(e) => setCompanyName(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && country && handleDiscover()}
            placeholder="e.g. Patagonia, Ecosia, Fairphone"
            className="hero-input"
            autoFocus
          />
        </div>

        <div>
          <label className="text-sm text-muted-foreground mb-2 block">Country</label>
          <Select value={country} onValueChange={setCountry} data-testid="country-select">
            <SelectTrigger className="h-14 rounded-xl bg-white/5 border-white/10 text-lg" data-testid="country-select-trigger">
              <SelectValue placeholder="Select country (EU/UK)" />
            </SelectTrigger>
            <SelectContent className="bg-card border-border max-h-[280px]">
              {EU_COUNTRIES.map((c) => (
                <SelectItem key={c} value={c} data-testid={`country-${c.toLowerCase().replace(/\s+/g, '-')}`}>
                  {c}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <Button
          data-testid="discover-btn"
          onClick={handleDiscover}
          disabled={loading || !companyName.trim() || !country}
          className="w-full h-14 rounded-full bg-primary text-primary-foreground text-lg font-medium btn-glow mt-4 disabled:opacity-40"
        >
          {loading ? (
            <span className="flex items-center gap-2">
              <span className="w-5 h-5 border-2 border-primary-foreground/30 border-t-primary-foreground rounded-full animate-spin" />
              Discovering your company...
            </span>
          ) : (
            <span className="flex items-center gap-2">
              <Search className="w-5 h-5" />
              Discover company
            </span>
          )}
        </Button>
      </div>
    </div>
  );
}

export default Step1CompanyDiscovery;
